/**
 * NEXUS UI - RadarChart
 * Hexagonal radar chart with animated data points
 */

import React, { useMemo } from 'react';
import type { BaseProps, NexusVariant, RadarDataPoint } from './types';

export interface RadarChartProps extends BaseProps {
  data: RadarDataPoint[];
  variant?: NexusVariant;
  size?: number;
  showLabels?: boolean;
  showValues?: boolean;
  animated?: boolean;
  rings?: number;
}

const variantColors: Record<NexusVariant, { stroke: string; fill: string; glow: string }> = {
  cyan: { stroke: '#00d4ff', fill: 'rgba(0, 212, 255, 0.15)', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { stroke: '#3b82f6', fill: 'rgba(59, 130, 246, 0.15)', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { stroke: '#a855f7', fill: 'rgba(168, 85, 247, 0.15)', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { stroke: '#00ff88', fill: 'rgba(0, 255, 136, 0.15)', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { stroke: '#ffaa00', fill: 'rgba(255, 170, 0, 0.15)', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { stroke: '#ff3366', fill: 'rgba(255, 51, 102, 0.15)', glow: 'rgba(255, 51, 102, 0.6)' },
};

export const RadarChart: React.FC<RadarChartProps> = ({
  data,
  variant = 'cyan',
  size = 200,
  showLabels = true,
  showValues = false,
  rings = 4,
  className = '',
  style,
}) => {
  const colors = variantColors[variant];
  const center = size / 2;
  const radius = (size - 60) / 2; // Leave room for labels
  const angleStep = (2 * Math.PI) / data.length;

  const { gridRings, gridLines, dataPath, dataPoints, labelPositions } = useMemo(() => {
    const getPoint = (value: number, maxValue: number, index: number) => {
      const normalizedValue = Math.min(value / maxValue, 1);
      const angle = angleStep * index - Math.PI / 2;
      const x = center + radius * normalizedValue * Math.cos(angle);
      const y = center + radius * normalizedValue * Math.sin(angle);
      return { x, y };
    };

    // Generate ring paths (hexagonal/polygonal grid)
    const gridRings = Array.from({ length: rings }).map((_, ringIndex) => {
      const ringRadius = ((ringIndex + 1) / rings) * radius;
      const points = data.map((_, dataIndex) => {
        const angle = angleStep * dataIndex - Math.PI / 2;
        return {
          x: center + ringRadius * Math.cos(angle),
          y: center + ringRadius * Math.sin(angle),
        };
      });
      return `M ${points.map(p => `${p.x},${p.y}`).join(' L ')} Z`;
    });

    // Generate axis lines from center to each vertex
    const gridLines = data.map((_, index) => {
      const angle = angleStep * index - Math.PI / 2;
      return {
        x1: center,
        y1: center,
        x2: center + radius * Math.cos(angle),
        y2: center + radius * Math.sin(angle),
      };
    });

    // Generate data polygon
    const dataPoints = data.map((d, index) => {
      const maxValue = d.maxValue ?? 100;
      return getPoint(d.value, maxValue, index);
    });

    const dataPath = `M ${dataPoints.map(p => `${p.x},${p.y}`).join(' L ')} Z`;

    // Label positions (slightly outside the grid)
    const labelPositions = data.map((d, index) => {
      const angle = angleStep * index - Math.PI / 2;
      const labelRadius = radius + 24;
      return {
        x: center + labelRadius * Math.cos(angle),
        y: center + labelRadius * Math.sin(angle),
        label: d.label,
        value: d.value,
        angle,
      };
    });

    return { gridRings, gridLines, dataPath, dataPoints, labelPositions };
  }, [data, radius, center, angleStep, rings]);

  const gradientId = `radar-gradient-${variant}-${Math.random().toString(36).slice(2, 9)}`;
  const glowId = `radar-glow-${variant}-${Math.random().toString(36).slice(2, 9)}`;

  return (
    <div
      className={`nx-radar-chart ${className}`}
      style={{
        width: '100%',
        maxWidth: size,
        ...style,
      }}
    >
      <svg
        width="100%"
        viewBox={`0 0 ${size} ${size}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <radialGradient id={gradientId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={colors.stroke} stopOpacity="0.3" />
            <stop offset="100%" stopColor={colors.stroke} stopOpacity="0.05" />
          </radialGradient>
          <filter id={glowId}>
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Grid rings */}
        {gridRings.map((ringPath, index) => (
          <path
            key={`ring-${index}`}
            d={ringPath}
            fill="none"
            stroke="rgba(0, 212, 255, 0.1)"
            strokeWidth={1}
          />
        ))}

        {/* Grid lines (axes) */}
        {gridLines.map((line, index) => (
          <line
            key={`line-${index}`}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke="rgba(0, 212, 255, 0.15)"
            strokeWidth={1}
          />
        ))}

        {/* Data fill */}
        <path
          d={dataPath}
          fill={`url(#${gradientId})`}
          stroke={colors.stroke}
          strokeWidth={2}
          filter={`url(#${glowId})`}
          style={{
            transformOrigin: `${center}px ${center}px`,
            /* Removed constant pulse - transitions on value changes provide sufficient feedback */
          }}
        />

        {/* Data points */}
        {dataPoints.map((point, index) => (
          <g key={`point-${index}`}>
            {/* Outer ring - static, no animation */}
            <circle
              cx={point.x}
              cy={point.y}
              r={6}
              fill="none"
              stroke={colors.stroke}
              strokeWidth={1}
              opacity={0.4}
            />
            {/* Point */}
            <circle
              cx={point.x}
              cy={point.y}
              r={4}
              fill={colors.stroke}
              filter={`url(#${glowId})`}
            />
          </g>
        ))}

        {/* Labels */}
        {showLabels &&
          labelPositions.map((label, index) => {
            const textAnchor =
              Math.abs(label.angle + Math.PI / 2) < 0.1
                ? 'middle'
                : label.angle > -Math.PI / 2 && label.angle < Math.PI / 2
                ? 'start'
                : 'end';

            return (
              <g key={`label-${index}`}>
                <text
                  x={label.x}
                  y={label.y}
                  textAnchor={textAnchor}
                  dominantBaseline="middle"
                  fill="var(--nx-text-secondary)"
                  fontSize="10"
                  fontFamily="var(--nx-font-interface)"
                  style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  {label.label}
                </text>
                {showValues && (
                  <text
                    x={label.x}
                    y={label.y + 12}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    fill={colors.stroke}
                    fontSize="11"
                    fontFamily="var(--nx-font-mono)"
                    fontWeight="600"
                  >
                    {label.value}
                  </text>
                )}
              </g>
            );
          })}

        {/* Center dot */}
        <circle cx={center} cy={center} r={3} fill={colors.stroke} opacity={0.5} />
      </svg>
    </div>
  );
};

export default RadarChart;
